import { useEffect, useState } from 'react'
import type { DarPreview, DarPreviewSite } from './types'
import { fetchDarPreview } from './useAdminApi'
import { AdminTable, Banner, Td, Th } from './ui'

/** Same live data the xlsx workbook renders from (serving/dar_report.py's
 *  build_dar_preview reuses fetch_dar_data unchanged) -- this view and the download
 *  never disagree. Mirrors the workbook's own two-page shape: an all-sites summary,
 *  then one stacked block per site broken down by category. */
export function DarPreviewView(): JSX.Element {
  const [preview, setPreview] = useState<DarPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchDarPreview()
      .then((p) => { if (live) setPreview(p) })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Failed to load preview') })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  if (loading) return <p className="text-sm text-slate-500">Loading preview…</p>
  if (error) return <Banner tone="error">{error}</Banner>
  if (!preview) return <Banner tone="error">No preview data.</Banner>

  const totalRow = (values: number[]): number => values.reduce((a, b) => a + b, 0)

  return (
    <div className="flex flex-col gap-8">
      <p className="text-xs text-slate-400">Run date {preview.run_date_label} · critical alerts only</p>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">Daily alert counts — current week</h3>
        <AdminTable>
          <thead>
            <tr>
              <Th>Day</Th>
              {preview.sites.map((s) => (
                <Th key={s.key} className="text-right">
                  <span className="inline-flex items-center gap-1.5 justify-end">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                    {s.name}
                  </span>
                </Th>
              ))}
              <Th className="text-right">Total</Th>
            </tr>
          </thead>
          <tbody>
            {preview.week_labels.map((label, i) => {
              const cells = preview.sites.map((s) => ({ key: s.key, value: s.week_total[i] ?? 0 }))
              return (
                <tr key={label}>
                  <Td>{label}</Td>
                  {cells.map((c) => <Td key={c.key} className="text-right" mono>{c.value}</Td>)}
                  <Td className="text-right font-semibold" mono>{totalRow(cells.map((c) => c.value))}</Td>
                </tr>
              )
            })}
          </tbody>
        </AdminTable>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-slate-800">Monthly total by site — trailing 6 months</h3>
        <AdminTable>
          <thead>
            <tr>
              <Th>Month</Th>
              {preview.sites.map((s) => <Th key={s.key} className="text-right">{s.name}</Th>)}
              <Th className="text-right">Total</Th>
            </tr>
          </thead>
          <tbody>
            {preview.month_labels.map((label, i) => {
              const cells = preview.sites.map((s) => ({ key: s.key, value: s.month_total[i] ?? 0 }))
              return (
                <tr key={label}>
                  <Td>{label}</Td>
                  {cells.map((c) => <Td key={c.key} className="text-right" mono>{c.value}</Td>)}
                  <Td className="text-right font-semibold" mono>{totalRow(cells.map((c) => c.value))}</Td>
                </tr>
              )
            })}
          </tbody>
        </AdminTable>
      </section>

      <section className="flex flex-col gap-6">
        <h3 className="text-sm font-semibold text-slate-800">Per-site detail</h3>
        {preview.sites.map((site) => <SiteDetail key={site.key} site={site} monthLabels={preview.month_labels} weekLabels={preview.week_labels} />)}
      </section>
    </div>
  )
}

function CategoryTable({ rowLabels, categories, byCategory }: {
  rowLabels: string[]
  categories: string[]
  byCategory: Record<string, number[]>
}): JSX.Element {
  return (
    <AdminTable>
      <thead>
        <tr>
          <Th />
          {categories.map((c) => <Th key={c} className="text-right">{c}</Th>)}
          <Th className="text-right">Total</Th>
        </tr>
      </thead>
      <tbody>
        {rowLabels.map((label, i) => {
          const values = categories.map((c) => byCategory[c]?.[i] ?? 0)
          const rowTotal = values.reduce((a, b) => a + b, 0)
          return (
            <tr key={label}>
              <Td>{label}</Td>
              {values.map((v, ci) => <Td key={categories[ci]} className="text-right" mono>{v}</Td>)}
              <Td className="text-right font-semibold" mono>{rowTotal}</Td>
            </tr>
          )
        })}
      </tbody>
    </AdminTable>
  )
}

function SiteDetail({ site, monthLabels, weekLabels }: {
  site: DarPreviewSite
  monthLabels: string[]
  weekLabels: string[]
}): JSX.Element {
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: site.color }} />
        {site.name}
      </h4>
      {site.categories.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-400">
          No CRITICAL alerts recorded in this period.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div>
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Month on month — by category</p>
            <CategoryTable rowLabels={monthLabels} categories={site.categories} byCategory={site.month_by_category} />
          </div>
          <div>
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Current week — by category</p>
            <CategoryTable rowLabels={weekLabels} categories={site.categories} byCategory={site.week_by_category} />
          </div>
        </div>
      )}
    </div>
  )
}
